import { Overlay, OverlayConfig, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { CdkPortal } from '@angular/cdk/portal';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { distinctUntilKeyChanged } from 'rxjs/operators';
import { fadeAnimation } from '../../utils/animations';

@Component({
  selector: 'app-popover',
  templateUrl: './popover.component.html',
  styleUrls: ['./popover.component.scss'],
  animations: [fadeAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PopoverComponent implements OnDestroy {
  @Input() reference!: HTMLElement;
  @Input() width!: string;
  @Input() anchor: 'left' | 'right' = 'right';
  @Input() disposeOnNavigation = true;
  @Output() positionChanged: EventEmitter<'top' | 'bottom'> = new EventEmitter();

  @ViewChild(CdkPortal) contentTemplate!: CdkPortal;

  private subscription$: Subscription = new Subscription();
  private overlayRef!: OverlayRef;
  public isVisible = false;

  constructor(private overlayPositionBuilder: OverlayPositionBuilder, private overlay: Overlay, private cdr: ChangeDetectorRef) {}

  private getOverlayConfig(): OverlayConfig {
    const left = [
      {
        // left bottom align
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
      },
      {
        // left top align
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
      },
    ] as any[];
    const right = [
      {
        // right bottom align
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
      },
      {
        // right top align
        originX: 'end',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
      },
    ] as any[];
    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.reference)
      .withPush(false)
      .withPositions(this.anchor === 'right' ? right : left);

    this.subscription$.add(
      positionStrategy.positionChanges.pipe(distinctUntilKeyChanged('connectionPair')).subscribe(({ connectionPair }) => {
        this.positionChanged.emit(connectionPair.originY === 'bottom' && connectionPair.overlayY === 'top' ? 'bottom' : 'top');
      }),
    );

    return new OverlayConfig({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      disposeOnNavigation: this.disposeOnNavigation,
    });
  }

  public open(): void {
    this.isVisible = true;
    this.overlayRef = this.overlay.create(this.getOverlayConfig());
    this.overlayRef.attach(this.contentTemplate);
    this.overlayRef.updateSize({ width: this.width ? this.width : this.reference.clientWidth });
    this.subscription$.add(this.overlayRef.backdropClick().subscribe(() => this.closeAnimation()));
  }

  public close(): void {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
    }
  }

  public closeAnimation(): void {
    this.isVisible = false;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.close();
    this.subscription$?.unsubscribe();
  }
}
